import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Api } from '@core/api/Api';
import { map, type Observable } from 'rxjs';
import type { EnrichedFinances } from './correction-tool.models';

export interface CorrectionDocument {
  _id: string;
  originalTransactionListId: string;
  extractedData: EnrichedFinances;
  correctedData: EnrichedFinances | null;
  extractionTimestamp: string;
  originalLockExpiresAt: string;
  correctionTimestamp: string | null;
  tenant: string;
  status: string;
  statusChangedBy: string;
  statusChangedAt: string;
  hidden: boolean;
}

export interface CorrectionResponse {
  correction: CorrectionDocument;
  docUrl: string;
  rotatedDocUrl: string;
}

interface BackendCorrectionResponse {
  data: EnrichedFinances;
  correctionId: string;
  originalTransactionListId: string;
  status: string;
  originalLockExpiresAt: string;
  docUrl: string;
  rotatedDocUrl: string;
  hasCorrectedData: boolean;
  extractionTimestamp: string;
  correctionTimestamp: string | null;
  statusChangedBy: string;
  statusChangedAt: string;
  hidden: boolean;
  tenant: string;
}

export interface CorrectionCount {
  open: { TOTAL: number };
  locked: { TOTAL: number };
}

@Injectable({
  providedIn: 'root',
})
export class CorrectionToolService {
  private readonly http = inject(HttpClient);

  private mapBackendResponse(
    res: BackendCorrectionResponse,
  ): CorrectionResponse {
    return {
      correction: {
        _id: res.correctionId,
        originalTransactionListId: res.originalTransactionListId,
        extractedData: res.hasCorrectedData ? null : res.data,
        correctedData: res.hasCorrectedData ? res.data : null,
        extractionTimestamp: res.extractionTimestamp,
        originalLockExpiresAt: res.originalLockExpiresAt,
        correctionTimestamp: res.correctionTimestamp,
        tenant: res.tenant,
        status: res.status,
        statusChangedBy: res.statusChangedBy,
        statusChangedAt: res.statusChangedAt,
        hidden: res.hidden,
      },
      docUrl: res.docUrl,
      rotatedDocUrl: res.rotatedDocUrl,
    };
  }

  getNext(): Observable<CorrectionResponse> {
    return this.http
      .get<BackendCorrectionResponse>(Api.Correction.Next)
      .pipe(map((res) => this.mapBackendResponse(res)));
  }

  complete(
    id: string,
    correctedData: EnrichedFinances,
    correctionStartedAt?: string,
  ): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(Api.Correction.Complete(id), {
      data: correctedData,
      correctionStartedAt,
    });
  }

  getCount(): Observable<CorrectionCount> {
    return this.http.get<CorrectionCount>(Api.Correction.Count);
  }

  getById(id: string): Observable<CorrectionResponse> {
    return this.http
      .get<BackendCorrectionResponse>(Api.Correction.Id(id))
      .pipe(map((res) => this.mapBackendResponse(res)));
  }

  lock(id: string): Observable<void> {
    return this.http.put<void>(Api.Correction.Lock(id), {});
  }

  unlock(id: string): Observable<void> {
    return this.http.delete<void>(Api.Correction.Unlock(id));
  }

  decline(id: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(Api.Correction.Decline(id), {});
  }

  sendToReview(id: string, comment: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(Api.Correction.Review(id), {
      comment,
    });
  }
}
